import {
  getOverrideFusionConfig,
  isStagingEnvironment,
} from '../api/overrideConfig';
import type { ValidateCodeRequestResponse } from './Offramp/api/merchant';
import { STORED_USER, currencyDetailProps } from './constant';
import { currencyInfo, SupportedCurrencies } from './constant';
import { v5 as uuidv5 } from 'uuid';

export const getPaymentParams = () => {
  const searchParams =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  const amount = searchParams?.get('amount');
  const currency = searchParams?.get('currency');
  const fromAmount = searchParams?.get('fromAmount') ?? '';
  const toAmount = searchParams?.get('toAmount') ?? '';
  const outgoingCurrency = searchParams?.get('outgoing_currency') ?? '';
  const incomingCurrency = searchParams?.get('incoming_currency') ?? '';
  const toCurrency = searchParams?.get('to_currency') ?? '';
  const fromCurrency = searchParams?.get('from_currency') ?? '';
  const toNetwork = searchParams?.get('to_network') ?? '';
  const fromNetwork = searchParams.get('from_network');
  const outgoingNetwork =
    searchParams?.get('outgoing_network') ??
    searchParams?.get('outgoingNetwork') ??
    '';
  const incomingNetwork =
    searchParams?.get('incoming_network') ??
    searchParams?.get('incomingNetwork') ??
    '';
  const v4FromNetwork =
    searchParams?.get('from_network') ??
    incomingNetwork ??
    searchParams?.get('fromNetwork') ??
    '';
  const v4ToNetwork =
    searchParams?.get('to_network') ??
    outgoingNetwork ??
    searchParams?.get('toNetwork') ??
    '';
  const rateKey = searchParams?.get('rate_key') ?? '';
  const fullname = searchParams?.get('fullname');
  const email =
    typeof localStorage !== 'undefined'
      ? localStorage?.getItem('email') ?? searchParams?.get('email')
      : searchParams?.get('email');
  const voucherId = searchParams?.get('voucherId');
  const merchant = searchParams?.get('user') ?? searchParams?.get('merchant');
  const bvn = searchParams?.get('bvn');
  const voucherType = searchParams?.get('voucherType');
  const redeemCurrency = searchParams?.get('redeemCurrency');
  const walletAddress =
    searchParams?.get('address') ??
    (searchParams?.get('redeemAddress') as `0x${string}` | null | undefined);
  const merchantDisplayName = searchParams?.get('displayName') ?? '';
  const mobileNetwork = searchParams?.get('mobileNetwork') ?? '';
  const phone = searchParams?.get('phone') ?? '';
  const reference = searchParams?.get('reference') ?? '';
  const mmoPaymentType = searchParams?.get('paymentType') ?? '';
  const paybillNumber = searchParams?.get('paybillNumber') ?? '';
  const paybillAccountNumber = searchParams?.get('paybillAccountNumber') ?? '';
  const isV4 = searchParams.get('isV4') === 'true';
  const v4FiatCurrency = (
    outgoingCurrency ||
    toCurrency ||
    'NGN'
  ).toUpperCase();
  const callback = searchParams?.get('callback');

  return {
    searchParams,
    bvn,
    email,
    amount,
    currency,
    isV4,
    fullname,
    voucherId,
    merchant,
    voucherType,
    walletAddress,
    redeemCurrency,
    mobileNetwork,
    phone,
    merchantDisplayName,
    outgoingCurrency,
    incomingCurrency,
    toCurrency,
    fromCurrency,
    toNetwork,
    fromNetwork,
    outgoingNetwork,
    incomingNetwork,
    v4FromNetwork,
    v4ToNetwork,
    v4FiatCurrency,
    rateKey,
    reference,
    callback,
    fromAmount,
    toAmount,
    mmoPaymentType,
    paybillNumber,
    paybillAccountNumber,
  };
};

export const getAuthorizationDetails = () => {
  const { merchant: user, searchParams } = getPaymentParams();
  const key = searchParams.get('key');
  return {
    user,
    key,
  };
};

export const getCallBack = () => {
  return getPaymentParams().callback;
};

export const truncateText = (text: string, start?: number, end?: number) => {
  const firstPart = text.slice(0, start || 4);
  const lastPart = text.slice(end || -5);
  return `${firstPart}...${lastPart}`;
};

export const formatTime = (time: any) => {
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const formatAmount = (amount: string) => {
  const cleanAmount = amount?.replace(/,/g, '');

  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(Number(cleanAmount ?? 0));
};

export const formatAmountWithDecimal = (
  amount: number,
  isFiatOrStable: boolean
) => {
  const cleanAmount = amount;

  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: isFiatOrStable ? 4 : 8,
  }).format(Number(cleanAmount));
};

export const hasExpired = (timestamp: number) => {
  const currentTime = new Date().getTime();

  return timestamp <= currentTime;
};

export const getCurrency = (
  currency: SupportedCurrencies
): currencyDetailProps => {
  return currencyInfo[currency];
};

export const getUserFromLocalStorage = () => {
  const store = localStorage.getItem(STORED_USER);
  const v4_user = localStorage.getItem('v4_user');

  let storedUser = null;
  let storedUser_v4 = null;
  if (store) {
    storedUser = JSON.parse(store) as ValidateCodeRequestResponse;
  }
  if (v4_user) {
    storedUser_v4 = JSON.parse(v4_user) as ValidateCodeRequestResponse;
  }
  return {
    storedUser,
    storedUser_v4,
  };
};

interface CountryInfo {
  countryCode: string;
}

const countryPhoneCodeMap: { [key: string]: CountryInfo } = {
  '+233': {
    countryCode: 'GH',
  },
};

export const getCountryByPhoneCode = (phoneCode: string) => {
  return countryPhoneCodeMap[phoneCode];
};
export const fiatCurrencies = ['KES', 'NGN', 'GHS'];

export const capitalizeFirst = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getUniqueCurrencies = (data?: {
  byNetWork?: Record<string, Record<string, { symbol?: string }>>;
}): string[] => {
  if (!data?.byNetWork) return [];

  const symbols = new Set<string>();

  for (const network of Object.values(data.byNetWork)) {
    for (const currency of Object.values(network)) {
      if (currency?.symbol && !fiatCurrencies.includes(currency?.symbol)) {
        symbols.add(currency.symbol);
      }
    }
  }

  return Array.from(symbols);
};

export const getSupportedNetworks = (
  currency: string,
  supportedAssets: any
): string[] | undefined => {
  let networks;
  if (supportedAssets?.byCurrency?.[currency]) {
    networks = Object?.keys(supportedAssets.byCurrency[currency]);
  }
  return networks;
};

export const getContractAddress = (
  data: any,
  network: string,
  currency: string
): any => {
  const networkData = data?.byNetWork?.[network?.toLowerCase()];
  if (!networkData)
    return {
      contractAddress: null,
      decimal: null,
    };
  const currencyData = networkData?.[currency?.toUpperCase()];
  if (!currencyData)
    return {
      contractAddress: null,
      decimal: null,
    };
  return {
    contractAddress: currencyData?.contractAddress || null,
    decimal: currencyData?.decimals || null,
  };
};

export const formatStringToPascalCase = (value: string) => {
  return value?.charAt(0)?.toUpperCase() + value?.slice(1);
};

export const copyToClipboard = (text: string) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      return true;
    })
    .catch((error) => {
      console.error('Error copying to clipboard: ', error);
      return false;
    });
};

export const generateUUIDFromString = (input: any): string => {
  const tenantId = isStagingEnvironment()
    ? getOverrideFusionConfig().tenantId ||
      process.env.REACT_APP_FUSION_TENANT_ID_V4 ||
      ''
    : process.env.REACT_APP_FUSION_TENANT_ID_V4 || '';
  return uuidv5(input, tenantId);
};

const FIAT_OR_STABLE = [
  'USD',
  'NGN',
  'GHS',
  'KES',
  'USDT',
  'USDC',
  'USDJ',
  'CUSD',
];
export const getIsFiatOrStable = (currency: string): boolean =>
  FIAT_OR_STABLE.includes(currency?.toUpperCase());
